import { useEffect, useRef, useState } from "react";
import "./AcedTooltip.scss";

export interface AcedTooltipProps {
  text: string;
  children: any;
}

const AcedTooltip: React.FC<AcedTooltipProps> = ({ text, children }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [canHover, setCanHover] = useState(true);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    const hoverMediaQuery = window.matchMedia("(hover: hover)");
    setCanHover(hoverMediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => {
      setCanHover(e.matches);
    };

    hoverMediaQuery.addEventListener("change", handler);
    return () => hoverMediaQuery.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (visible && tooltipRef.current) {
      const handleScroll = () => {
        setVisible(false);
      };

      window.addEventListener("scroll", handleScroll, true);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [visible]);

  useEffect(() => {
    if (visible && tooltipRef.current && iconRef.current) {
      const tooltipEl = tooltipRef.current;
      const iconEl = iconRef.current;
      const iconRect = iconEl.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();

      let top = iconRect.bottom + 8;
      let left = iconRect.left + iconRect.width / 2 - tooltipRect.width / 2;

      if (left < 0) left = 8;
      if (left + tooltipRect.width > window.innerWidth)
        left = window.innerWidth - tooltipRect.width - 8;
      if (top + tooltipRect.height > window.innerHeight)
        top = iconRect.top - tooltipRect.height - 8;

      setPosition({ top, left });
    }
  }, [visible]);

  const handleMouseEnter = () => {
    if (!canHover) return;
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (!canHover) return;
    setVisible(false);
    clearTimeout(timeoutRef.current);
  };

  const handleTouchStart = () => {
    if (canHover) return;
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, 400); // long press threshold
  };

  const handleTouchEnd = () => {
    if (canHover) return;
    clearTimeout(timeoutRef.current);
    setTimeout(() => setVisible(false), 800);
  };

  return (
    <>
      <div
        ref={iconRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {children}
      </div>
      {visible && (
        <div
          ref={tooltipRef}
          className="aced-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            whiteSpace: "pre-line",
            textAlign: "center",
            zIndex: 1000,
          }}
        >
          {text}
        </div>
      )}
    </>
  );
};

export default AcedTooltip;
